import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Formik, Field, Form, ErrorMessage } from "formik";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Modal, { ModalBody } from '../../components/common/Modal';
import { login, mfa } from "../../slices/auth/authSlice";
import Loader from "../../components/common/Loader";
import ImageComponent from '../../components/common/Image';
import { setDealerData, setDealerType } from '../../slices/dashboard/dashboardSlice';
import { LoginSchema } from '../../schema/loginSchema';
import backgroundVideo from '../../assets/img/login_bg_2.mp4'
import { dealerCheck } from '../../slices/eligibility/eligibilitySlice';
import { isValidPartnerSlug } from '../../partner'; // ✅ shared, validated partner check

const Login = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [timer, setTimer] = useState("00:00:00");
  const Ref = useRef(null);
  const [isMobileLogin, setIsMobileLogin] = useState(false);
  const [loader, setLoader] = useState(false);
  const [loginPayload, setLoginPayload] = useState(null);
  const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
  const [passwordValue, setPasswordValue] = useState('');
  const [show2FA, setShow2FA] = useState(false);
  const [loginResponse, setLoginResponse] = useState(null); // Store login response
  const [showPassword, setShowPassword] = useState(false);
  const [selectedLoginType, setSelectedLoginType] = useState(null); // 'employee' or 'dealer'
  const token = useSelector((state) => state.auth.accessToken);
  const urlParams = new URLSearchParams(window.location.search);
  const sourceId = sessionStorage.getItem('partnerSlug');
  const href = "#"

  // ✅ Raw value from storage
  const partnerSlug = sessionStorage.getItem('partnerSlug');
  // ✅ Validated against the known partner list — never trust raw storage directly
  const isPartnerLogin = isValidPartnerSlug(partnerSlug);

  // Auto-focus first OTP input when mobile login modal opens
  useEffect(() => {
    if (showModal && isMobileLogin) {
      setTimeout(() => {
        const firstOtpInput = document.querySelector('input[data-index="0"]');
        if (firstOtpInput) {
          firstOtpInput.focus();
        }
      }, 100);
    }
  }, [showModal, isMobileLogin]);

  // Auto-focus first OTP input when 2FA section shows
  useEffect(() => {
    if (show2FA) {
      setTimeout(() => {
        const firstOtpInput = document.querySelector('input[data-index="0"]');
        if (firstOtpInput) {
          firstOtpInput.focus();
        }
      }, 100);
    }
  }, [show2FA]);

  const initialValues = {
    username: '',
    mobileNumber: '',
    password: '',
    usertype: '',
    rememberMe: false
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleLoginTypeSelect = (type) => {
    // 🔒 Guard: partner-linked sessions can only proceed as dealer
    if (isPartnerLogin && type === 'employee') return;
    setSelectedLoginType(type);
  };

  const handleClose = () => {
    if (showModal) {
      if (show2FA) {
        // Go back from 2FA to password screen
        setShow2FA(false);
        setOtpValues(['', '', '', '', '', '']);
      } else {
        // Close modal and reset states
        setShowModal(false);
        setIsMobileLogin(false);
        setLoginPayload(null);
        setOtpValues(['', '', '', '', '', '']);
        setPasswordValue('');
        setLoginResponse(null);
        setLoader(false);
      }
    } else if (selectedLoginType) {
      // Go back from login form to login type selection
      setSelectedLoginType(null);
    }
  };

  // Function to check if the input is a mobile number
  const isMobileNumber = (value) => {
    const mobileRegex = /^[6-9]\d{9}$/; // Indian mobile number pattern
    return mobileRegex.test(value);
  };


  const getUserLocation = async (shouldUseGeolocation = true) => {
    if (!shouldUseGeolocation) {
      // Prevent unnecessary access
      return Promise.reject(new Error("Geolocation access is not necessary."));
    }

    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return Promise.reject(new Error("Geolocation is not supported by your browser"));
    }

    return new Promise((resolve, reject) => {
      // ⚠️ Only call this if geolocation is truly necessary
      //NOSONAR
      navigator.geolocation.getCurrentPosition( //NOSONAR
        (position) => {
          resolve(position.coords);
        },
        (err) => {
          if (err.code === 1) {
            toast.error('Please allow location access to continue');
          } else {
            toast.error('Error getting location');
          }
          reject(err);
        }
      );
    });
  };


  const handleLogin = async (formikProps) => {
    const errors = await formikProps.validateForm();
    if (Object.keys(errors).length > 0) {
      // Set touched for all fields with errors to show validation messages
      const touchedFields = {};
      Object.keys(errors).forEach(field => {
        touchedFields[field] = true;
      });
      formikProps.setTouched(touchedFields);

      // Focus on first error field
      const firstErrorField = Object.keys(errors)[0];
      const fieldElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (fieldElement) {
        fieldElement.focus();
        window.scrollTo({
          top: fieldElement.offsetTop - 100,
          behavior: 'smooth',
        });
      }
      return;
    }

    const isLoginIdMobile = isMobileNumber(formikProps.values.username);
    setIsMobileLogin(isLoginIdMobile);

    if (selectedLoginType === 'dealer') {
      // Dealer flow: If mobile number, make API call immediately for OTP
      if (isLoginIdMobile) {
        setLoader(true);
        try {
          const mobilePayload = {
            type: "mobile",
            username: formikProps.values.username,
            usertype: "dealer"
          };

          const response = await dispatch(login({
            data: mobilePayload,
            encrypt: true
          })).unwrap();

          if (response?.response_code === "S100") {
            // toast.success(response?.response_string);
            // Store payload and show modal for OTP entry
            setLoginPayload({
              username: formikProps.values.username,
              usertype: "dealer"
            });
            setLoginResponse(response);
            setShowModal(true);
            setOtpValues(['', '', '', '', '', ''])
            setTimer("02:00");
            clearTimer(getDeadTime());
          } else {
            !response?.payload?.error &&
              toast.error(response?.response_string);
          }
        } catch (error) {
        } finally {
          setLoader(false);
        }
      } else {
        // For dealer non-mobile login (Dealer ID), still go to OTP flow
        setLoader(true);
        try {
          const dealerPayload = {
            type: "dealer",
            username: formikProps.values.username,
            usertype: "dealer"
          };

          const response = await dispatch(login({
            data: dealerPayload,
            encrypt: true
          })).unwrap();

          if (response?.response_code === "S100") {
            // toast.success(response?.response_string);
            // Store payload and show modal for OTP entry
            setLoginPayload({
              username: formikProps.values.username,
              usertype: "dealer"
            });
            setLoginResponse(response);
            setIsMobileLogin(true); // Treat as OTP flow for dealers
            setShowModal(true);
            setOtpValues(['', '', '', '', '', ''])
            setTimer("01:00");
            clearTimer(getDeadTime());
          } else {
            !response?.payload?.error &&
              toast.error(response?.response_string);
          }
        } catch (error) {
        } finally {
          setLoader(false);
        }
      }
    } else {
      // Chola Emp flow: Always go to Password -> 2FA (even for mobile numbers)
      const payload = {
        username: formikProps.values.username,
        password: formikProps.values.password || '',
        isMobileLogin: false, // Always treat as password flow for employees
        usertype: "employee"
      };

      setLoginPayload(payload);
      setShow2FA(false);
      setIsMobileLogin(false); // Ensure it goes to password flow
      setShowModal(true);
    }
  };

  const handleOtpChange = (index, value) => {
    // Allow only digits
    if (!/^\d*$/.test(value)) return;

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value.slice(-1); // Ensure only the last digit is used
    setOtpValues(newOtpValues);

    // Auto-move to next input
    if (value && index < otpValues.length - 1) {
      const nextInput = document.querySelector(`input[data-index="${index + 1}"]`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    const key = e.key;

    if (key === 'Backspace') {
      if (otpValues[index]) {
        // Clear current value
        const newOtpValues = [...otpValues];
        newOtpValues[index] = '';
        setOtpValues(newOtpValues);
      } else if (index > 0) {
        // Move focus to previous input
        const prevInput = document.querySelector(`input[data-index="${index - 1}"]`);
        if (prevInput) prevInput.focus();
      }
    }

    if (key === 'ArrowLeft' && index > 0) {
      const prevInput = document.querySelector(`input[data-index="${index - 1}"]`);
      if (prevInput) prevInput.focus();
    }

    if (key === 'ArrowRight' && index < otpValues.length - 1) {
      const nextInput = document.querySelector(`input[data-index="${index + 1}"]`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').trim().replace(/\D/g, ''); // numbers only
    if (paste.length === otpValues.length) {
      setOtpValues(paste.split(''));
      // Optional: move focus to last input
      const lastInput = document.querySelector(`input[data-index="${otpValues.length - 1}"]`);
      if (lastInput) lastInput.focus();
    }
  };

  const handleSubmit = async () => {
    if (!loginPayload) return;

    setLoader(true);

    try {
      if (isMobileLogin) {
        // For mobile login, directly call MFA verify with OTP
        await handleVerify();
      } else {
        // For non-mobile login, proceed with regular login flow
        const finalPayload = {
          ...loginPayload,
          username: loginPayload.username,
          password: passwordValue,
          otp: otpValues.join(''),
          source: sourceId || 'chola',
          twoFactorCode: otpValues.join(''),
          usertype: loginPayload.usertype || "employee"
        };

        const response = await dispatch(login({
          data: finalPayload,
          encrypt: true
        })).unwrap();

        if (response?.response_code === "S100") {
          setLoginResponse(response);
          setShow2FA(true);
          setOtpValues(['', '', '', '', '', '']);
          // toast.success(response?.response_string);
        } else {
          !response?.payload?.error &&
            toast.error(response?.response_string);
          setShow2FA(false);
        }
      }
    } catch (error) {
      setShow2FA(false);
    } finally {
      setLoader(false);
    }
  };

  const getTimeRemaining = (e) => {
    const total = Date.parse(e) - Date.parse(new Date());  // Get total remaining time in milliseconds
    const seconds = Math.floor((total / 1000) % 60);  // Remaining seconds
    const minutes = Math.floor((total / 1000 / 60) % 60);  // Remaining minutes

    return { total, minutes, seconds };
  };

  // Function to start the timer and update the state
  const startTimer = (e) => {
    let { total, minutes, seconds } = getTimeRemaining(e);
    if (total >= 0) {
      // Update the timer if time is still remaining
      setTimer(
        (minutes > 9 ? minutes : "0" + minutes) + ":" + (seconds > 9 ? seconds : "0" + seconds)
      );
    } else {
      // If time is over, clear the interval
      clearInterval(Ref.current);
    }
  };

  // Function to clear the previous interval and start a new one
  const clearTimer = (e) => {
    setTimer("01:00");  // Reset timer to 120 seconds (2 minutes)

    // Clear the previous interval (if any)
    if (Ref.current) clearInterval(Ref.current);

    // Set a new interval to update the timer every second
    const id = setInterval(() => {
      startTimer(e);
    }, 1000);
    Ref.current = id;  // Store the interval ID in Ref
  };

  // Function to get the deadline (current time + 120 seconds)
  const getDeadTime = () => {
    let deadline = new Date();
    deadline.setSeconds(deadline.getSeconds() + 60);  // Add 120 seconds (2 minutes)
    return deadline;
  };


  const handleVerify = async () => {
    if (!loginResponse || !loginPayload) {
      toast.error('Missing login information. Please try again.');
      return;
    }

    setLoader(true);

    try {
      // Get user location
      const location = await getUserLocation();

      // Prepare MFA verification payload
      const mfaPayload = {
        username: loginPayload.username,
        password: isMobileLogin ? '' : passwordValue,
        code: otpValues.join(''), // 2FA code from OTP inputs
        reference_id: loginResponse.response_data.reference_id,
        latitude: location?.latitude,
        longitude: location?.longitude,
        type: isMobileLogin ? "mobile" : "2FA",
        source: sourceId ? sourceId : 'chola',
        usertype: loginPayload.usertype || (isMobileLogin ? "dealer" : "employee")
      };

      // Call MFA verification API
      const response = await dispatch(mfa({
        data: mfaPayload,
        encrypt: true // This tells axios to encrypt the payload
      })).unwrap();

      if (response?.response_code === "S100" || response?.status === true) {
        setShowModal(false);
        dispatch(setDealerData(response?.response_data?.dealer_data))
        dispatch(setDealerType(response?.response_data?.userType))
        navigate('/dashboard');
      } else {
        !response?.payload?.error &&
          toast.error(response?.response_string || response?.message);
      }
    } catch (error) {
    } finally {
      setLoader(false);
    }
  };

  // Update the existing useEffect for token check
  useEffect(() => {
    if (token) {
      navigate('/dashboard');
    } else {
      // Reset all states and clean up modal backdrop when there's no token (after logout)
      setShowModal(false);
      setIsMobileLogin(false);
      setLoader(false);
      setLoginPayload(null);
      setOtpValues(['', '', '', '', '', '']);
      setPasswordValue('');
      setShow2FA(false);
      setLoginResponse(null);
      setSelectedLoginType(null);

      // Remove modal backdrop if it exists
      const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop) {
        backdrop.remove();
      }

      // Remove modal-open class from body
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  }, [token, navigate]);


  return (
    <>
      <div className='container-fluid'>
        <div className="row">
          <div className="col-md-5 col-lg-7 LoginBg d-sm-none d-md-block d-lg-block">
            <div className="welcome">
              <video
                className="welcome__bg-video"
                autoPlay
                muted
                loop
                playsInline
              >
                <source src={backgroundVideo} type="video/mp4" />
                Your browser does not support the video tag.
              </video>

              <div className="welcome__content">
                <div className="d-flex align-items-center justify-content-center login-logo-div">
                  <a href={href}>
                    <ImageComponent
                      fileName="cholacd.svg"
                      className="img-fluid"
                      style={{ height: '64px' }}
                      alt="cholacd"
                    />
                  </a>

                  {/* ✅ Conditional Partner Logo — only rendered for a validated partner slug */}
                  {isPartnerLogin && (
                    <>
                      {/* Optional separator line or text if you want it between logos */}
                      <span className="mx-3 text-muted" style={{ fontSize: '24px' }}>|</span>

                      <a href={href}>
                          <img
                            src={`https://d1bwtttozpp5m5.cloudfront.net/cdlos/product_images/${partnerSlug}.png`}
                            className="img-fluid"
                            style={{ height: '32px' }}
                            alt="partner-logo"
                          />
                      </a>
                    </>
                  )}
                </div>
              </div>
            </div>

          </div>
          <div className="col-md-7 col-lg-5 d-flex justify-content-center">
            <div className="d-flex align-items-center">
              <div className="form-login">
                <Formik
                  validationSchema={LoginSchema.schema}
                  initialValues={initialValues}
                  enableReinitialize={true}
                  validateOnMount={true}
                  validateOnBlur={true}
                  validateOnChange={true}
                >
                  {(formikProps) => {
                    return (
                      <Form>
                        <div className="d-flex align-items-center">
                          <div className="form-login">
                            {selectedLoginType && (
                              <button
                                type="button"
                                className="btn-close position-absolute"
                                style={{ top: '10px', right: '10px', zIndex: 1000 }}
                                onClick={handleClose}
                                aria-label="Close"
                              ></button>
                            )}
                            <p className="text-center mb-0">Let's get started...</p>
                            <p className="text-center fw-500">Select login account</p>
                            <div className="selector-wrapper">
                              <div className="toggle-container">
                                <div
                                  className="position-relative"
                                  style={{
                                    // 🔒 Greyed out whenever a validated partner slug is active,
                                    // in addition to the existing dealer-selected state
                                    opacity: (selectedLoginType === 'dealer' || isPartnerLogin) ? '0.2' : '1',
                                    pointerEvents: (selectedLoginType === 'dealer' || isPartnerLogin) ? 'none' : 'auto',
                                    cursor: (selectedLoginType === 'dealer' || isPartnerLogin) ? 'default' : 'pointer'
                                  }}
                                  onClick={() => {
                                    if (isPartnerLogin) return; // 🔒 hard guard against bypassing pointerEvents
                                    formikProps?.setFieldValue('usertype', 'employee');
                                    handleLoginTypeSelect('employee');
                                  }
                                  }
                                  onKeyDown={(e) => {
                                    if (isPartnerLogin) return; // 🔒 hard guard for keyboard activation
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleLoginTypeSelect('employee');
                                    }
                                  }}
                                  tabIndex={isPartnerLogin ? -1 : 0} // 👈 removed from tab order when disabled
                                  role="button"
                                  aria-disabled={isPartnerLogin}
                                >
                                  <ImageComponent fileName="cholaside.svg" />
                                  <p className="emp-chola"><span className="icon-arrowleftl1 mx-2 fw-bold"></span>Chola Emp</p>
                                </div>
                                <p className="seperator">or</p>
                                <div
                                  className="position-relative"
                                  style={{
                                    opacity: selectedLoginType === 'employee' ? '0.2' : '1',
                                    pointerEvents: selectedLoginType === 'employee' ? 'none' : 'auto',
                                    cursor: selectedLoginType === 'employee' ? 'default' : 'pointer'
                                  }}
                                  onClick={() => {
                                    formikProps?.setFieldValue('usertype', 'dealer');
                                    handleLoginTypeSelect('dealer')
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleLoginTypeSelect('dealer');
                                    }
                                  }}
                                  tabIndex={0}
                                  role="button"
                                >
                                  <ImageComponent fileName="chola-right.svg" />
                                  <p className="emp-deler-chola">Dealer<span className="icon-arrowright-L1 mx-2 fw-bold"></span></p>
                                </div>
                              </div>
                            </div>

                            <div className={`custom-form mt-4 ${selectedLoginType ? '' : 'd-none'}`}>
                              <div className="col-12">
                                <div className="input-container  mt-3 has-icon">
                                  <Field type="text" name="username" className="form-control input-underline custom-login" id="floatingInputUsername" placeholder=' ' required />
                                  <label htmlFor="input" className="label form-label login-label">
                                    {selectedLoginType === 'employee' ? 'Emp ID/ Email ID/ Mobile Number' : 'User ID/ Mobile Number'}
                                  </label>
                                  <div className="underline"></div>
                                  <div className="has-i-login emp-login-icon">
                                    <span className=" icon-conduct-profile fs-2"></span>
                                  </div>
                                  <ErrorMessage name="username" component="div" className="text-danger small mt-2" />
                                </div>
                              </div>

                              <div className="d-none">
                                <h4 className="fw-medium">Sign In with Mobile Number</h4>

                                <div className="form-floating mb-3">
                                  <Field type="text" name="mobileNumber" className="form-control" id="mobileInput" placeholder="Mobile Number" />
                                  <label className="form-label" htmlFor="mobileInput">Mobile Number</label>
                                  <div className="has-i-login">
                                    <span className="icon-call fs-2"></span>
                                  </div>
                                  <ErrorMessage name="mobileNumber" component="div" className="text-danger small mt-2" />
                                </div>
                              </div>

                              <div className="form-group mt-2">
                                <div className="form-check d-flex">
                                  <Field
                                    type="checkbox"
                                    name="rememberMe"
                                    className="form-check-input"
                                    id="flexCheckDefault"
                                  />
                                  <label className="form-check-label small" htmlFor="flexCheckDefault">
                                    Remember me
                                  </label>
                                </div>
                              </div>

                              <div className="d-flex justify-content-end mt-4">
                                <button className="btn btn-primary btn-lg w-100 signin" type="button" onClick={() => handleLogin(formikProps)}>
                                  Sign In
                                </button>
                              </div>
                            </div>
                          </div>
                          <Modal
                            id="SelectRole"
                            className="SelectRole"
                            labelledBy="SelectRole"
                            modalClass="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg bottom-modal"
                            show={showModal}
                          >
                            <ModalBody>
                              <div className="row">
                                <button
                                  type="button"
                                  className="btn-close position-absolute"
                                  style={{ top: '10px', right: '10px', zIndex: 1000 }}
                                  onClick={handleClose}
                                  aria-label="Close"
                                ></button>

                                {isMobileLogin ? (
                                  <>
                                    <div className="col-lg-12 col-md-12 col-sm-12 mb-3">
                                      <ImageComponent fileName="eligibleotp.svg" alt="eligibleotp" />
                                    </div>
                                    {/* Mobile OTP Section */}
                                    <div className="col-lg-12 col-md-12 col-sm-12 mb-3">
                                      <p className="form-label fw-semibold fs-6 d-none">Please enter OTP</p>
                                      <p className="form-label fs-7">
                                        Please enter OTP - sent to registered mobile no.
                                      </p>

                                      <div className="mt-2">
                                        <p className="fw-semibold form-label fs-7">
                                          {loginPayload?.username}<span className="icon-Contentedit-2" role="button"
                                            tabIndex={0}
                                            onClick={() => setShowModal(false)}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setShowModal(false);
                                              }
                                            }}
                                            style={{ cursor: 'pointer', marginLeft: '8px' }}></span>
                                        </p>
                                      </div>

                                      <div className="col-lg-12 col-md-12 col-sm-12 mt-3">
                                        <div>
                                          <div className="d-flex gap-3 justify-content-center mb-4">
                                            {otpValues.map((value, index) => (
                                              <input
                                                key={index}
                                                type="tel"
                                                maxLength="1"
                                                value={value}
                                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                onPaste={handleOtpPaste}
                                                data-index={index}
                                                className="otp-in input-underline otp-input"
                                                inputMode="numeric"
                                                autoComplete="one-time-code"
                                              />
                                            ))}
                                          </div>
                                        </div>
                                        {timer !== "00:00" ? (
                                          `Resend OTP in ${timer} seconds`
                                        ) : (
                                          <p className="fw-semibold form-label fs-7" role="button"
                                            tabIndex={0}
                                            onClick={() => handleLogin(formikProps)}
                                            onKeyDown={(e) => {
                                              if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleLogin(formikProps)
                                              }
                                            }}>
                                            Resend OTP
                                          </p>)}
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="col-lg-12 col-md-12 col-sm-12 mb-3 text-center justify-content-center password-img-center">
                                      <ImageComponent fileName="2fverify.svg" alt="2fverify" />
                                    </div>
                                    {/* Password and 2FA Section */}
                                    <div className="col-lg-12 col-md-12 col-sm-12 mb-3">
                                      {!show2FA && (
                                        <>
                                          <p className="fw-semibold fs-6 lh-1 text-center">Please Enter Password</p>
                                          <div className="input-container mt-3">
                                            <input className="password-custom" type={showPassword ? "text" : "password"} id="input" required="" value={passwordValue} autoComplete='off'
                                              onChange={(e) => setPasswordValue(e.target.value)} />
                                            <label htmlFor="input" className="label form-label d-none">Password</label>

                                            <div
                                              className="has-i-password"
                                              role="button"
                                              tabIndex={0}
                                              onClick={togglePasswordVisibility}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                  e.preventDefault();
                                                  togglePasswordVisibility();
                                                }
                                              }}
                                              style={{ cursor: 'pointer' }}
                                            >
                                              <span className={`fs-2 ${showPassword ? 'icon-securityeye' : 'icon-securityeye-slash'}`}></span>
                                            </div>
                                            <div className="underline"></div>
                                          </div>
                                          <div className="form-floating mb-3 mt-3">
                                            <div className="text-end mt-3">
                                              <a href={"https://adss.netaccess-india.com/authorization.do"} className="text-decoration-none bg-purple-txt small">Forgot Password?</a>
                                            </div>
                                          </div>
                                        </>
                                      )}

                                      {/* Conditionally show 2FA section based on show2FA state */}
                                      {show2FA && (
                                        <>
                                          <p className="fw-semibold fs-6 lh-1 text-center">Please enter the 6 digit code from your authenticator app</p>
                                          <div className="col-lg-12 col-md-12 col-sm-12 mt-3">
                                            <div>
                                              <div className="d-flex gap-3 justify-content-center mb-4">
                                                {otpValues.map((value, index) => (
                                                  <input
                                                    key={index}
                                                    type="tel"
                                                    className="otp-in input-underline otp-input"
                                                    data-index={index}
                                                    value={value}
                                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                    maxLength="1"
                                                  />
                                                ))}
                                              </div>
                                            </div>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </>
                                )}

                                <div className="col-lg-12 col-md-12 col-sm-12 mb-3">
                                  {/* Conditionally show Submit or Verify button */}
                                  {!show2FA ? (
                                    <button
                                      className="btn btn-primary btn-lg w-100 signin"
                                      type="button"
                                      onClick={handleSubmit}
                                      disabled={loader}
                                    >
                                      Submit
                                    </button>
                                  ) : (
                                    <button
                                      className="btn btn-primary btn-lg w-100 signin"
                                      type="button"
                                      onClick={handleVerify}
                                      disabled={loader}
                                    >
                                      {loader ? 'Verifying...' : 'Verify'}
                                    </button>
                                  )}
                                </div>

                              </div>
                            </ModalBody>
                          </Modal>
                        </div>
                      </Form>
                    )
                  }}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>

      {loader && <Loader />}
    </>
  );
}

export default Login;